import React, { useState } from 'react'
import { BackendKeywordTag } from '../../constants/tags'
import {
  getKeywordSizeLevel,
  getLocalizedName,
  keywordButtonFontSize,
  keywordButtonPadding,
} from './utils'
import { getRandomTagDelay, getTagAnimation } from './filterAnimation'

interface KeywordTagProps {
  keyword: BackendKeywordTag
  language: string
  minCount: number
  maxCount: number
  onSelect: (keywordId: number) => void
}

const KeywordTag = ({ keyword, language, minCount, maxCount, onSelect }: KeywordTagProps) => {
  const localizedName = getLocalizedName(keyword, language)
  const sizeLevel = getKeywordSizeLevel(keyword.count, minCount, maxCount)
  // picked once: a new delay on a re-render would re-time the fade in, and could hide a shown tag again
  const [fadeInDelay] = useState(getRandomTagDelay)

  const style = {
    '--tag-font-size': keywordButtonFontSize(sizeLevel),
    '--tag-animation': getTagAnimation(fadeInDelay),
    borderRadius: Math.max(4, 10 + sizeLevel * 2),
    padding: keywordButtonPadding(sizeLevel),
  } as React.CSSProperties

  // besides the fade in, only the hover and press feedback is animated: a size change (e.g. the root
  // font size settling as the filter opens) must not play as an animation.
  // The fade in is set through a variable, so a class can switch it off for reduced motion
  return (
    <button
      className="m-0 inline-flex w-auto max-w-[min-content] cursor-pointer gap-[4px] whitespace-nowrap border border-solid border-borderGrey bg-white text-center font-approach-regular text-[length:var(--tag-font-size)] leading-[1.3] text-black87 [animation:var(--tag-animation)] [transition:background-color_0.2s_ease,border-color_0.2s_ease,color_0.2s_ease,transform_0.2s_ease] hover:border hover:border-solid hover:border-red hover:bg-red hover:text-[rgba(255,255,255,0.9)] hover:[transform:scale(1.05)] active:[transform:scale(0.98)] motion-reduce:[animation:none] max-sm:text-[length:calc(var(--tag-font-size)*0.85)] lg:text-[length:calc(var(--tag-font-size)*1.15)] xxl:text-[length:calc(var(--tag-font-size)*1.3)]"
      style={style}
      data-id={keyword.id}
      data-count={keyword.count}
      data-size-level={sizeLevel}
      onClick={() => onSelect(keyword.id)}
    >
      <span className="font-approach-bold">{localizedName}</span>
      <span className="keywordCount">({keyword.count})</span>
    </button>
  )
}

export default React.memo(KeywordTag)
